/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { createDisposer, logDebug, type Unsubscribe } from '@amazon/vinyl-util'
import type { Player, PlaylistController } from '@amzn/amp-playback'
import { modalVisible } from '@/style/modalVisible'
import { navigateTo } from '@/router/router'
import { documentEvents, isTextEntry } from '@/util/interaction'
import { ctx } from '@/util/context'
import { toggleTrackLike } from '@/track/trackLikeService'
import { focusAgentChat } from '@/blackbolt/chatInputFocus'
import { focusSearchInput } from '@/search/searchState'
import { featureEnabled } from '@/core/featureGate'
import { advanceLyricsSection } from '@/nowPlaying/lyricsSectionController'
import { guardedSkipForward } from '@/playback/skipGuard'
import { stepAudiobookNext } from '@/transport/sectionNav'
import { previousOrRestart } from '@/transport/previousOrRestart'
import { withUserSkip } from '@/playback/metrics/userSkipSignal'
import { isTv } from '@/device/deviceConfig'

const { target, errorHandler } = ctx('keyboardShortcuts')

const DESKTOP_SHORTCUT_KEYS: readonly string[] = [
    'Space',
    'ArrowRight',
    'ArrowLeft',
    'KeyP',
    'KeyM',
    'KeyL',
    'KeyN',
    '/',
    '?',
]

/**
 * Event `code`/`key` values the global shortcuts act on. Smart TVs get none:
 * the remote's D-pad belongs to spatial focus and its media keys to
 * `mediaKeyBinding`.
 */
export function globalShortcutKeys(tv: boolean): readonly string[] {
    return tv ? [] : DESKTOP_SHORTCUT_KEYS
}

export interface KeyboardShortcutsDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
}

/**
 * Global keyboard shortcuts for transport control. Mirrors Hornet's
 * `useKeyboardShortcuts` — one document-level `keydown` listener that
 * fires the transport actions and bails on typing / modal states so it
 * can't fight the user's text input.
 *
 * Desktop only — on smart TVs nothing is bound (see {@link globalShortcutKeys}).
 *
 * Bindings:
 *   Space        — toggle play / pause
 *   ArrowRight   — skip to next entry
 *   ArrowLeft    — skip to previous entry (or seek to 0 past 3s)
 *   KeyP         — open the now-playing queue view
 *   KeyM         — toggle mute
 *   KeyL         — toggle like on the current track
 *   Shift+KeyN   — advance to the next lyrics section (skipped when no
 *                  LyricsView is mounted)
 *   /            — focus the agent chat input (amp on) or the header
 *                  search input (amp off)
 *   ?  (Shift+/) — open the keyboard shortcut help overlay
 *
 * Bails when:
 *   - a modifier key is held (so Cmd-R / Ctrl-F / etc still work) —
 *     the Shift-only bindings below live on their own branch that
 *     runs BEFORE the modifier bail, since they're intentionally
 *     Shift-keyed,
 *   - a modal is open (the top-of-stack owns the keyboard), including
 *     `?` itself — pressing `?` while the help overlay (or any other
 *     modal) is already open is swallowed, not treated as a close,
 *   - focus is in an editable surface (`<input>`, `<textarea>`,
 *     `<select>`, `[contenteditable]`) so typing a space in the chat
 *     entry bar doesn't pause playback,
 *   - for Space, focus sits on any interactive control — a button, link,
 *     `role="slider"`, or a focusable card/renderer — so its native
 *     activation is the only action, never playback on top of it;
 *   - for the arrows, focus sits on a control that uses arrows itself
 *     (slider, grouped choice, list, form field, focusable card). A plain
 *     button or link does not, so a clicked button never blocks next/previous.
 */
export function installGlobalKeyboardShortcuts(deps: KeyboardShortcutsDeps): Unsubscribe {
    const disposer = createDisposer()
    const { add, dispose } = disposer
    const { playerPromise, playlistController } = deps
    const shortcutKeys = globalShortcutKeys(isTv())
    if (shortcutKeys.length === 0) return dispose

    playerPromise
        .then((player) => {
            if (disposer.disposed) return
            logDebug(target, 'installing global keyboard shortcuts')

            add(
                documentEvents.on('keydown', (event: KeyboardEvent) => {
                    if (!shortcutKeys.includes(event.code) && !shortcutKeys.includes(event.key)) {
                        return
                    }
                    const eventTarget = event.target as HTMLElement | null

                    if (isTextEntry(eventTarget)) return

                    // Shift-keyed bindings run before the modifier bail —
                    // they intentionally require Shift. Cmd/Ctrl/Alt
                    // still disqualify (leave OS/browser shortcuts
                    // alone).
                    if (event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
                        if (modalVisible.value) return
                        // `?` on US layouts fires `code: 'Slash'` with
                        // `shiftKey: true`. `event.key === '?'` also
                        // works but layouts vary — key check is the
                        // most portable.
                        if (event.key === '?') {
                            event.preventDefault()
                            event.stopPropagation()
                            // Lazy — the help overlay (and its Modal chrome)
                            // only loads on the first `?` press, keeping it
                            // out of the app-shell chunk.
                            import('@/components/KeyboardShortcutHelp')
                                .then((m) => m.openKeyboardShortcutHelp())
                                .catch(errorHandler)
                            return
                        }
                        if (event.code === 'KeyN') {
                            if (advanceLyricsSection()) {
                                event.preventDefault()
                                event.stopPropagation()
                            }
                            return
                        }
                    }

                    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
                    if (modalVisible.value) return

                    // Key (not code) check: `/` arrives as `Slash`,
                    // `NumpadDivide`, or layout-specific codes — same
                    // portability reasoning as the `?` binding above.
                    // preventDefault also suppresses Firefox's
                    // quick-find, which claims bare `/`.
                    if (event.key === '/') {
                        event.preventDefault()
                        event.stopPropagation()
                        // Amp on: the bottom chat bar owns input. Amp off: the
                        // header search input is restored, so focus it instead.
                        featureEnabled('amp')
                            .then((enabled) => (enabled ? focusAgentChat() : focusSearchInput()))
                            .catch(errorHandler)
                        return
                    }

                    const active = document.activeElement
                    // Playback keys (Space / arrows) are suppressed when an
                    // interactive control owns focus, so the control's own
                    // activation is the ONLY action — a focused card's Space
                    // used to fire both its activation and play/pause.
                    if (
                        isTransportKey(event.code) &&
                        (ownsKey(eventTarget, event.code) || ownsKey(active, event.code))
                    ) {
                        return
                    }

                    switch (event.code) {
                        case 'Space': {
                            event.preventDefault()
                            event.stopPropagation()
                            if (active instanceof HTMLElement) active.blur()
                            if (player.paused$.value) player.play().catch(errorHandler)
                            else player.pause()
                            break
                        }
                        case 'ArrowRight':
                            event.preventDefault()
                            event.stopPropagation()
                            // Audiobooks step chapters within the player.
                            if (stepAudiobookNext(player, playlistController.current.value)) break
                            // Gate on skip capability — a capped / ad
                            // entry surfaces the upsell instead of
                            // skipping.
                            if (!guardedSkipForward(playlistController)) break
                            withUserSkip('next', () => playlistController.skipNext()).catch(
                                errorHandler
                            )
                            break
                        case 'ArrowLeft':
                            event.preventDefault()
                            event.stopPropagation()
                            previousOrRestart(player, playlistController).catch(errorHandler)
                            break
                        case 'KeyP':
                            event.preventDefault()
                            event.stopPropagation()
                            if (!window.location.pathname.startsWith('/now-playing')) {
                                navigateTo('/now-playing')
                            }
                            break
                        case 'KeyM':
                            event.preventDefault()
                            event.stopPropagation()
                            // Straight flip on the shared muted cell. Any
                            // volume-control UI observes the same cell, so
                            // both surfaces stay in sync.
                            player.muted$.value = !player.muted$.value
                            break
                        case 'KeyL': {
                            const entry = playlistController.current.value
                            const track = entry?.entity.__typename === 'Track' ? entry.entity : null
                            if (!track?.id) return
                            event.preventDefault()
                            event.stopPropagation()
                            // toggleTrackLike reads the liked state off
                            // the queue entity's payload (not overlay),
                            // so rapid presses may not alternate until
                            // the entity refreshes from the server.
                            toggleTrackLike(track)?.done.catch(errorHandler)
                            break
                        }
                    }
                })
            )
        })
        .catch(errorHandler)

    return dispose
}

const TRANSPORT_KEYS = new Set(['Space', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'])

function isTransportKey(code: string): boolean {
    return TRANSPORT_KEYS.has(code)
}

// One precise selector so `closest` matches only genuinely-interactive
// ancestors — a nearest non-interactive `[role="presentation"]` can't
// shadow a real button/card above it. `[tabindex]:not([tabindex^="-"])`
// keeps focusable cards/renderers (>= 0) while dropping the roving
// `tabindex="-1"` sinks that aren't user-actionable.
const INTERACTIVE_SELECTOR =
    'button, a[href], input, textarea, select, [contenteditable=""], [contenteditable="true"], ' +
    '[role="button"], [role="link"], [role="menuitem"], [role="slider"], [role="tab"], ' +
    '[role="option"], [role="checkbox"], [role="radio"], [role="switch"], ' +
    '[tabindex]:not([tabindex^="-"])'

/**
 * Controls that use the arrow keys themselves: sliders, grouped choices (radio,
 * tab, option, menu item), lists and grids, and form fields. Focusable
 * cards/renderers count too (see {@link isFocusableCard}). Plain buttons and links
 * are deliberately absent — they do nothing with arrows, and treating them as
 * owners meant a click on any button (the full-screen toggle, play/pause) left
 * focus there and silently disabled next/previous until the user clicked away.
 */
const ARROW_OWNER_SELECTOR =
    'input, textarea, select, [contenteditable=""], [contenteditable="true"], ' +
    '[role="slider"], [role="spinbutton"], [role="radio"], [role="radiogroup"], [role="tab"], ' +
    '[role="tablist"], [role="option"], [role="listbox"], [role="menuitem"], ' +
    '[role="menuitemradio"], [role="menuitemcheckbox"], [role="menu"], [role="grid"], ' +
    '[role="tree"]'

/** A focusable card/renderer (`tabindex` >= 0) that isn't simply a button or link. */
function isFocusableCard(el: Element | null): boolean {
    const card = el?.closest('[tabindex]:not([tabindex^="-"])')
    if (!card) return false
    const role = card.getAttribute('role')
    if (role === 'button' || role === 'link') return false
    return card.tagName !== 'BUTTON' && !(card.tagName === 'A' && card.hasAttribute('href'))
}

/**
 * Whether a focused control owns `code`, so the global playback binding must
 * stand aside. Space belongs to any interactive control (it activates buttons,
 * links, and focusable cards); arrows only to controls that use them. Walks
 * ancestors so a target nested in a focusable card still counts.
 */
function ownsKey(el: Element | null, code: string): boolean {
    if (el == null) return false
    if (code === 'Space') return el.closest(INTERACTIVE_SELECTOR) != null
    return el.closest(ARROW_OWNER_SELECTOR) != null || isFocusableCard(el)
}
